import React from 'react'

import { GuideSummaryProps } from '@cinch/oxd-components/guide-summary/src/guide-summary.component'
import Link from '@cinch/oxd-components/link/src'
import { OurCommitmentManufacturer as DealerOurCommitmentManufacturer } from '@cinch/oxd-components/dealer-our-commitment/src'
import { OurCommitmentManufacturer } from '@cinch/oxd-components/our-commitment/src'
import { Text, TextStyles } from '@cinch-labs/design-system'
import Wrapper from '@cinch/oxd-components/wrapper/src'
import Layout from '@cinch/oxd-layouts/regular/src'
import manufacturers from '@cinch/oxd-utils/manufacturers/src/data'
import { queryString } from '@cinch/oxd-utils/requests/src'
import { FEATURES, hasFeatureFlag } from '@cinch/oxd-utils/feature-flags/src'

import Intro from '@cinch/oxd-components/manufacturer-intro/src'
import Vehicles from '@cinch/oxd-components/manufacturer-vehicles/src'
import DealerVehicles from '@cinch/oxd-components/dealer-manufacturer-vehicles/src'
import Guides from './components/guides'
import Jargon from './components/jargon'

import * as styles from './manufacturer.module.css'

export interface ManufacturerPageProps {
  i18n: Cinch.WebAppTranslations['manufacturer']
  bodyText: React.ReactNode
  makeKey: string
  manufacturerName: string
  models: Array<{ label: string; value: string }>
  guides: GuideSummaryProps[]
  jargon: Array<{ question: string; summary: string; id: string; uid: string }>
}

const ManufacturerPage: React.FC<ManufacturerPageProps> = ({
  i18n,
  bodyText,
  makeKey,
  manufacturerName,
  models,
  guides,
  jargon,
}) => {
  const [vehiclesAvailable, setVehiclesAvailable] = React.useState<boolean>(false)

  const modelUrl = (model: string) => `/cars/${makeKey}/${model}`.replace(/\s+/g, '-').toLowerCase()

  const manufacturerUrl = (make: string) => {
    return `/find-vehicle?${queryString({ make, pageSize: 24 })}`
  }

  const handleVehiclesAvailability = (isAvailability: boolean) => {
    setVehiclesAvailable(isAvailability)
  }

  return (
    <Layout isFluid={true}>
      <Wrapper>
        <Intro
          title={manufacturerName}
          body={bodyText}
          searchUrl={manufacturerUrl(makeKey)}
          vehiclesAvailable={vehiclesAvailable}
          i18n={i18n}
        />

        {hasFeatureFlag(FEATURES.CAMBRIDGE) ? (
          <Vehicles
            car={{ manufacturerName }}
            title={manufacturerName}
            i18n={i18n}
            onVehiclesAvailability={handleVehiclesAvailability}
          />
        ) : (
          <DealerVehicles
            car={{ manufacturerName }}
            title={manufacturerName}
            i18n={i18n}
            onVehiclesAvailability={handleVehiclesAvailability}
          />
        )}
      </Wrapper>

      <div className={styles.models}>
        <Wrapper>
          <Text oxford={true} className={styles.makeModelsTitle} element={'h3'} type={TextStyles.vehicleSubTitle}>
            {manufacturerName} {i18n.models}
          </Text>
          <ul className={styles.makeModelList}>
            {models.map((model) => (
              <li data-testid="model-link" className={styles.makeModelListItem} key={model.value}>
                <Link oxford={true} aria-label={model.label} to={modelUrl(model.value)}>
                  {model.label}
                </Link>
              </li>
            ))}
          </ul>
        </Wrapper>
      </div>

      <Wrapper>
        <div data-testid="our-commitment" className={styles.commitment}>
          {hasFeatureFlag(FEATURES.CAMBRIDGE) ? <OurCommitmentManufacturer /> : <DealerOurCommitmentManufacturer />}
        </div>
      </Wrapper>

      {!!guides && !!guides.length && (
        <Wrapper>
          <Guides i18n={i18n} guides={guides} />
        </Wrapper>
      )}

      {!!jargon && !!jargon.length && (
        <Wrapper>
          <Jargon i18n={i18n} jargon={jargon} />
        </Wrapper>
      )}

      <div className={styles.makes}>
        <Wrapper>
          <Text oxford={true} className={styles.makeModelsTitle} element={'h3'} type={TextStyles.vehicleSubTitle}>
            {i18n.seeOther}
          </Text>
          <ul className={styles.makeModelList}>
            {Object.keys(manufacturers).map((key) => (
              <li data-testid="make-link" className={styles.makeModelListItem} key={key}>
                <Link
                  oxford={true}
                  aria-label={manufacturers[key].displayName}
                  to={`/cars/${key.toLowerCase().replace(' ', '-')}`}
                >
                  {manufacturers[key].displayName}
                </Link>
              </li>
            ))}
          </ul>
        </Wrapper>
      </div>
    </Layout>
  )
}

export default ManufacturerPage
